/**
 * Intake Gaps — the chase list (Slice M / ADR-0100). Turns the firm roster's
 * ✓/✗ artifact data into "who do we chase for what", grouped by artifact with
 * counts. Rows are DERIVED, not Tasks — there is no Mark done here; a gap
 * closes when the document is uploaded (ADR-0086 panel affordance), the
 * primary contact is linked, or the artifact is marked not required.
 *
 * Layout, top to bottom:
 *   - Six artifact groups (fixed order), each a heading with a count and an
 *     expandable firm list (name · designation · age-in-days). Zero-count
 *     groups render too — the page tells the truth about what was checked
 *     (Slice G posture). Firm rows deep-link to the Firm panel
 *     (`/firm-success/firms?firm=<id>` — Slice I's search-param door).
 *   - An "Onboarding" section for firms younger than 14 days with gaps —
 *     separate visual treatment, excluded from the monthly Slack post
 *     (locked decision 3).
 *   - All-caught-up empty state when zero gaps AND zero onboarding rows
 *     (locked decision 5).
 *
 * Groups with firms default expanded — the page IS the chase list; hiding
 * the names behind six clicks would defeat it. Empty groups have nothing to
 * expand.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import {
  INTAKE_ARTIFACT_LABELS,
  type IntakeArtifact,
  type IntakeGap,
} from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ClientSuccessNav } from "@/components/client-success-nav";
import { useIntakeGaps } from "@/lib/use-intake-gaps";
import { cn } from "@/lib/utils";

export function IntakeGapsPage() {
  const query = useIntakeGaps();

  return (
    <div className="p-6">
      <ClientSuccessNav active="intake-gaps" />
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-xl tracking-tight">Intake Gaps</h1>
        {query.data ? (
          <span
            className="text-xs text-plane-muted"
            data-testid="intake-gaps-generated-at"
          >
            as of {new Date(query.data.generatedAt).toLocaleString()}
          </span>
        ) : null}
      </div>

      {query.isLoading ? (
        <div
          className="text-sm text-plane-muted"
          data-testid="intake-gaps-loading"
        >
          Loading…
        </div>
      ) : query.isError || !query.data ? (
        <div className="text-sm text-error" data-testid="intake-gaps-error">
          Couldn't load Intake Gaps. It will retry automatically.
        </div>
      ) : (
        <IntakeGapsBody
          gaps={query.data.gaps}
          onboarding={query.data.onboarding.firms}
        />
      )}
    </div>
  );
}

function IntakeGapsBody({
  gaps,
  onboarding,
}: {
  gaps: IntakeGap[];
  onboarding: Array<{
    id: string;
    name: string;
    ageDays: number;
    missing: IntakeArtifact[];
  }>;
}) {
  const totalGaps = gaps.reduce((sum, g) => sum + g.firms.length, 0);

  if (totalGaps === 0 && onboarding.length === 0) {
    return (
      <Card className="p-8 text-center" data-testid="intake-gaps-empty">
        <Check className="mx-auto h-8 w-8 text-success" aria-hidden="true" />
        <div className="mt-2 text-sm font-medium">All caught up</div>
        <div className="mt-1 text-xs text-muted-foreground">
          No firms are missing required intake artifacts.
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="intake-gaps">
      {gaps.map((gap) => (
        <GapGroup key={gap.artifact} gap={gap} />
      ))}
      {onboarding.length > 0 ? (
        <section
          className="rounded border border-dashed border-foreground/20 bg-muted/30 p-3"
          data-testid="intake-gaps-onboarding"
          aria-labelledby="intake-gaps-onboarding-heading"
        >
          <h2
            id="intake-gaps-onboarding-heading"
            className="text-sm font-sans font-semibold"
          >
            Onboarding{" "}
            <span className="font-normal text-plane-muted">
              — firms younger than 14 days; not chased yet, excluded from the
              monthly post
            </span>
          </h2>
          <div className="mt-2 space-y-1">
            {onboarding.map((firm) => (
              <div
                key={firm.id}
                className="flex items-center justify-between gap-3 text-sm"
                data-testid={`onboarding-firm-${firm.id}`}
              >
                <Link
                  to="/firm-success/firms"
                  search={{ firm: firm.id }}
                  className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
                >
                  {firm.name}
                </Link>
                <span className="text-xs text-plane-muted">
                  {firm.ageDays}d · missing{" "}
                  {firm.missing
                    .map((a) => INTAKE_ARTIFACT_LABELS[a])
                    .join(", ")}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function GapGroup({ gap }: { gap: IntakeGap }) {
  // Groups WITH firms open by default — this page is the chase list.
  const [expanded, setExpanded] = useState(gap.firms.length > 0);
  const count = gap.firms.length;
  return (
    <section
      data-testid={`gap-group-${gap.artifact}`}
      aria-labelledby={`gap-heading-${gap.artifact}`}
    >
      <button
        type="button"
        id={`gap-heading-${gap.artifact}`}
        onClick={() => setExpanded((v) => !v)}
        disabled={count === 0}
        aria-expanded={expanded}
        className={cn(
          "flex w-full items-center gap-2 text-left text-sm font-semibold",
          count === 0 && "cursor-default text-plane-muted",
        )}
        data-testid={`gap-toggle-${gap.artifact}`}
      >
        <span>{INTAKE_ARTIFACT_LABELS[gap.artifact]}</span>
        <Badge variant={count === 0 ? "secondary" : "default"}>
          {count}
        </Badge>
        {count > 0 ? (
          <span className="text-xs font-normal text-plane-muted">
            {expanded ? "hide" : "show"}
          </span>
        ) : null}
      </button>
      {expanded && count > 0 ? (
        <div className="mt-2 space-y-1">
          {gap.firms.map((firm) => (
            <Card
              key={firm.id}
              className="flex items-center justify-between gap-3 p-2 text-sm"
              data-testid={`gap-firm-${gap.artifact}-${firm.id}`}
            >
              <Link
                to="/firm-success/firms"
                search={{ firm: firm.id }}
                className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
                data-testid={`gap-firm-link-${gap.artifact}-${firm.id}`}
              >
                {firm.name}
              </Link>
              <span className="text-xs text-muted-foreground">
                {firm.designation ?? "—"} · waiting {firm.gapAgeDays}d
              </span>
            </Card>
          ))}
        </div>
      ) : null}
    </section>
  );
}
